/**
 * Catalog context, a clinic's Social Links (H59 part 3). Pure.
 *
 * **A fixed set, in a fixed order.** The order is the one every client draws
 * the icons in, so it is a rule rather than a styling choice: two clients
 * listing the same links differently is the same clinic described twice.
 *
 * **Each link must be on its own platform's domain**, so a link cannot wear one
 * platform's icon and open another site. A subdomain counts (`www.`, `m.`), a
 * look-alike does not: `instagram.com.example.net` ends in `example.net`.
 */
import type { FieldCode } from "@/modules/shared/failure";

export const SOCIAL_PLATFORMS = [
  "instagram",
  "facebook",
  "tiktok",
  "linkedin",
  "x",
  "youtube",
] as const;

export type SocialPlatform = (typeof SOCIAL_PLATFORMS)[number];

export interface SocialLink {
  platform: SocialPlatform;
  url: string;
}

/** The platform's name as people write it, for labels and screen readers. */
export const SOCIAL_PLATFORM_NAMES: Record<SocialPlatform, string> = {
  instagram: "Instagram",
  facebook: "Facebook",
  tiktok: "TikTok",
  linkedin: "LinkedIn",
  x: "X",
  youtube: "YouTube",
};

/** The form field each platform's link is posted under. */
export const SOCIAL_FIELD_NAMES: Record<SocialPlatform, string> = {
  instagram: "socialInstagram",
  facebook: "socialFacebook",
  tiktok: "socialTiktok",
  linkedin: "socialLinkedin",
  x: "socialX",
  youtube: "socialYoutube",
};

const DOMAINS: Record<SocialPlatform, readonly string[]> = {
  instagram: ["instagram.com"],
  facebook: ["facebook.com", "fb.com"],
  tiktok: ["tiktok.com"],
  linkedin: ["linkedin.com"],
  x: ["x.com", "twitter.com"],
  youtube: ["youtube.com", "youtu.be"],
};

const INVALID: Record<SocialPlatform, FieldCode> = {
  instagram: "social_instagram_invalid",
  facebook: "social_facebook_invalid",
  tiktok: "social_tiktok_invalid",
  linkedin: "social_linkedin_invalid",
  x: "social_x_invalid",
  youtube: "social_youtube_invalid",
};

/** Mirrors the column's check. */
const MAX_URL_LENGTH = 500;

/** Whether `raw` is an http(s) link on `platform`'s own domain. */
export function isPlatformUrl(platform: SocialPlatform, raw: string): boolean {
  if (raw.length > MAX_URL_LENGTH) return false;
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return false;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return false;
  const host = url.hostname.toLowerCase();
  return DOMAINS[platform].some(
    (domain) => host === domain || host.endsWith(`.${domain}`),
  );
}

export type SocialLinksResult =
  | { ok: true; links: SocialLink[] }
  | { ok: false; fieldErrors: Record<string, FieldCode> };

/**
 * Read the six optional fields. A blank field is no link; every link that is
 * there must be its platform's. The result is in `SOCIAL_PLATFORMS` order.
 */
export function parseSocialLinks(raw: Record<string, unknown>): SocialLinksResult {
  const links: SocialLink[] = [];
  const fieldErrors: Record<string, FieldCode> = {};
  for (const platform of SOCIAL_PLATFORMS) {
    const field = SOCIAL_FIELD_NAMES[platform];
    const value = raw[field];
    if (value == null) continue;
    if (typeof value !== "string") {
      fieldErrors[field] = INVALID[platform];
      continue;
    }
    const url = value.trim();
    if (url === "") continue;
    if (!isPlatformUrl(platform, url)) {
      fieldErrors[field] = INVALID[platform];
      continue;
    }
    links.push({ platform, url });
  }
  return Object.keys(fieldErrors).length > 0
    ? { ok: false, fieldErrors }
    : { ok: true, links };
}
